import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';

const SectionDivider = ({ icon, title }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.4rem 0.8rem', borderRadius: '6px',
        background: 'linear-gradient(90deg, #f1f5f9, #f8fafc)',
        borderLeft: '3px solid #6366f1', margin: '0.25rem 0 0.75rem'
    }}>
        <i className={icon} style={{ color: '#6366f1', fontSize: '0.8rem' }} />
        <span style={{ fontWeight: '600', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {title}
        </span>
    </div>
);

const FieldLabel = ({ children, required }) => (
    <label style={{ fontWeight: '600', fontSize: '0.8rem', color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
        {children} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
);

export default function Compnaymodal({ visible, onHide, companyData, onSave, clients, allBranchs, alllocation, allplatform }) {
    const [company, setCompany] = useState(companyData);
    const [submitted, setSubmitted] = useState(false);
    const [filteredBranchs, setFilteredBranchs] = useState([]);
    const [filteredLocation, setFilteredLocation] = useState([]);

    // Check if logged-in branch is SR1
    const isSR1 = useMemo(() => {
        const branchName = localStorage.getItem('branch') || localStorage.getItem('branchname') || '';
        return branchName.trim().toUpperCase() === 'SR1';
    }, []);

    const toId = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : v;
    };

    useEffect(() => {
        if (companyData) {
            const data = { ...companyData };
            if (data.effective_from) data.effective_from = new Date(data.effective_from);

            if (!data.company_id) {
                // SR1: fill client from session
                if (isSR1) {
                    const storedClientId = localStorage.getItem('clientid');
                    if (storedClientId) data.client_id = toId(storedClientId);
                } else {
                    // Auto-select first client
                    const clientList = clients || [];
                    if (clientList.length > 0 && !data.client_id) {
                        data.client_id = toId(clientList[0].client_id);
                    }
                }

                // Auto-select first platform
                const platforms = allplatform || [];
                if (platforms.length > 0 && !data.platform_id) {
                    data.platform_id = platforms[0].platform_id;
                }

                // Compute branch options for the selected client, defaulting to the first branch
                if (data.client_id) {
                    const branches = (allBranchs || []).filter(
                        (b) => String(b.client_id) === String(data.client_id)
                    );
                    setFilteredBranchs(branches);
                    if (!data.branch_id && branches.length > 0) {
                        data.branch_id = branches[0].branch_id;
                    }
                }

                // Compute location options for the selected branch, defaulting to the first location
                if (data.branch_id) {
                    const locs = (alllocation || []).filter(
                        (l) => String(l.branch_id) === String(data.branch_id)
                    );
                    setFilteredLocation(locs);
                    if (!data.location_id && locs.length > 0) {
                        data.location_id = locs[0].location_id;
                    }
                }
            }

            setCompany(data);
        }
    }, [companyData]);

    // Update branch options when client changes (user-initiated)
    useEffect(() => {
        if (company?.client_id) {
            const branches = (allBranchs || []).filter((b) => String(b.client_id) === String(company.client_id));
            setFilteredBranchs(branches);
            if (!company.company_id && branches.length > 0 && !company.branch_id) {
                setCompany((prev) => ({ ...prev, branch_id: branches[0].branch_id }));
            }
        } else {
            setFilteredBranchs([]);
        }
    }, [company?.client_id, allBranchs]);

    // Update location options when branch changes (user-initiated)
    useEffect(() => {
        if (company?.branch_id) {
            setFilteredLocation((alllocation || []).filter((l) => String(l.branch_id) === String(company.branch_id)));
        } else {
            setFilteredLocation([]);
        }
    }, [company?.branch_id, alllocation]);

    // Auto-select first location when locations update (user-initiated branch change)
    useEffect(() => {
        if (!company?.company_id && filteredLocation.length > 0 && !company?.location_id) {
            setCompany((prev) => ({ ...prev, location_id: filteredLocation[0].location_id }));
        }
    }, [filteredLocation]);

    // Fill client/platform auto-selections when master data loads asynchronously
    useEffect(() => {
        if (!visible || company?.company_id) return;
        setCompany((prev) => {
            const updates = {};
            if (!prev.client_id && !isSR1 && clients?.length > 0) {
                updates.client_id = toId(clients[0].client_id);
            }
            if (!prev.platform_id && allplatform?.length > 0) {
                updates.platform_id = allplatform[0].platform_id;
            }
            return Object.keys(updates).length ? { ...prev, ...updates } : prev;
        });
    }, [visible, clients, allplatform]);

    const onInputChange = (val, name) => {
        const _company = { ...company };
        if ((name === 'phone_number' || name === 'alernative_mobile') && val && !/^\d{0,10}$/.test(val)) return;
        if (name === 'gst_no' && val && !/^[a-zA-Z0-9]{0,15}$/.test(val)) return;
        _company[name] = val;
        if (name === 'client_id') { _company.branch_id = null; _company.location_id = null; }
        if (name === 'branch_id') { _company.location_id = null; }
        setCompany(_company);
    };

    const isInvalid = (name) => {
        if (!submitted) return false;
        const val = company?.[name];
        if (name === 'company_name') return !val?.trim();
        if (['platform_id', 'client_id', 'branch_id', 'location_id'].includes(name)) return !val;
        if (name === 'phone_number' || name === 'alernative_mobile') return val && val.length !== 10;
        if (name === 'gst_no') return val && val.length !== 15;
        return false;
    };

    const handleSave = () => {
        setSubmitted(true);
        const mandatory = company?.company_name?.trim() && company?.platform_id && company?.client_id && company?.branch_id && company?.location_id;
        const formats = (!company?.phone_number || company.phone_number.length === 10)
            && (!company?.alernative_mobile || company.alernative_mobile.length === 10)
            && (!company?.gst_no || company.gst_no.length === 15);
        if (mandatory && formats) {
            onSave(company);
            setSubmitted(false);
        }
    };

    const isEdit = !!company?.company_id;

    const dialogHeader = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{
                width: '2.25rem', height: '2.25rem', flexShrink: 0,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                <i className="pi pi-building" style={{ color: '#fff', fontSize: '0.95rem' }} />
            </span>
            <div style={{ lineHeight: 1.3 }}>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                    {isEdit ? 'Edit Company' : 'Add New Company'}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    {isEdit ? 'Update existing company record' : 'Fill in the details to create a company'}
                </div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button label="Cancel" icon="pi pi-times" outlined severity="secondary" onClick={onHide} style={{ minWidth: '100px' }} />
            <Button label={isEdit ? 'Update' : 'Save'} icon="pi pi-check" severity="success" onClick={handleSave} style={{ minWidth: '100px' }} />
        </div>
    );

    if (!company) return null;

    return (
        <Dialog
            visible={visible}
            style={{ width: '680px' }}
            breakpoints={{ '768px': '92vw' }}
            header={dialogHeader}
            modal
            className="p-fluid"
            footer={dialogFooter}
            onHide={onHide}
            contentStyle={{ padding: '1rem 1.25rem 0.75rem' }}
        >
            {/* ── Business Information ─────────────────── */}
            <SectionDivider icon="pi pi-briefcase" title="Business Information" />
            <div className="grid" style={{ margin: 0 }}>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Company Name</FieldLabel>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-building" />
                        <InputText
                            value={company.company_name || ''}
                            onChange={(e) => onInputChange(e.target.value, 'company_name')}
                            placeholder="Enter company name"
                            className={classNames('w-full', { 'p-invalid': isInvalid('company_name') })}
                        />
                    </span>
                    {isInvalid('company_name') && <small className="p-error">Company name is required.</small>}
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Platform</FieldLabel>
                    <Dropdown
                        value={company.platform_id}
                        options={allplatform}
                        onChange={(e) => onInputChange(e.value, 'platform_id')}
                        optionLabel="platform_name"
                        optionValue="platform_id"
                        placeholder="Select platform"
                        style={isSR1 ? { pointerEvents: 'none' } : undefined}
                        className={classNames({ 'p-invalid': isInvalid('platform_id') })}
                        filter
                    />
                    {isInvalid('platform_id') && <small className="p-error">Platform is required.</small>}
                </div>


                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Branch</FieldLabel>
                    <Dropdown
                        value={company.branch_id}
                        options={filteredBranchs}
                        onChange={(e) => onInputChange(e.value, 'branch_id')}
                        optionLabel="branch_name"
                        optionValue="branch_id"
                        placeholder={company.client_id ? 'Select branch' : 'Select client first'}
                        disabled={!company.client_id}
                        className={classNames({ 'p-invalid': isInvalid('branch_id') })}
                        filter
                    />
                    {isInvalid('branch_id') && <small className="p-error">Branch is required.</small>}
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Location</FieldLabel>
                    <Dropdown
                        value={company.location_id}
                        options={filteredLocation}
                        onChange={(e) => onInputChange(e.value, 'location_id')}
                        optionLabel="location_name"
                        optionValue="location_id"
                        placeholder={company.branch_id ? 'Select location' : 'Select branch first'}
                        disabled={!company.branch_id}
                        className={classNames({ 'p-invalid': isInvalid('location_id') })}
                        filter
                    />
                    {isInvalid('location_id') && <small className="p-error">Location is required.</small>}
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Company Address</FieldLabel>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-map-marker" />
                        <InputText
                            value={company.company_address || ''}
                            onChange={(e) => onInputChange(e.target.value, 'company_address')}
                            placeholder="Enter address"
                            className="w-full"
                        />
                    </span>
                </div>
            </div>

            {/* ── Contact & Settings ──────────────────── */}
            <SectionDivider icon="pi pi-phone" title="Contact & Settings" />
            <div className="grid" style={{ margin: 0 }}>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Mobile Number</FieldLabel>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-phone" />
                        <InputText
                            value={company.phone_number || ''}
                            onChange={(e) => onInputChange(e.target.value, 'phone_number')}
                            placeholder="10-digit number"
                            keyfilter="num"
                            maxLength={10}
                            className={classNames('w-full', { 'p-invalid': isInvalid('phone_number') })}
                        />
                    </span>
                    {isInvalid('phone_number') && <small className="p-error">Must be exactly 10 digits.</small>}
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Alternate Mobile</FieldLabel>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-mobile" />
                        <InputText
                            value={company.alernative_mobile || ''}
                            onChange={(e) => onInputChange(e.target.value, 'alernative_mobile')}
                            placeholder="10-digit number"
                            keyfilter="num"
                            maxLength={10}
                            className={classNames('w-full', { 'p-invalid': isInvalid('alernative_mobile') })}
                        />
                    </span>
                    {isInvalid('alernative_mobile') && <small className="p-error">Must be exactly 10 digits.</small>}
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>GSTIN</FieldLabel>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-id-card" />
                        <InputText
                            value={company.gst_no || ''}
                            onChange={(e) => onInputChange(e.target.value, 'gst_no')}
                            placeholder="15-character GST number"
                            maxLength={15}
                            className={classNames('w-full', { 'p-invalid': isInvalid('gst_no') })}
                        />
                    </span>
                    {isInvalid('gst_no') && <small className="p-error">Must be exactly 15 characters.</small>}
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Effective From</FieldLabel>
                    <Calendar
                        value={company.effective_from}
                        onChange={(e) => onInputChange(e.value, 'effective_from')}
                        dateFormat="dd/mm/yy"
                        showIcon
                        placeholder="dd/mm/yyyy"
                        className="w-full"
                    />
                </div>

                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Status</FieldLabel>
                    <Dropdown
                        value={company.compmay_status}
                        options={[
                            { label: 'Active', value: 'Active' },
                            { label: 'Inactive', value: 'Inactive' }
                        ]}
                        onChange={(e) => onInputChange(e.value, 'compmay_status')}
                        placeholder="Select status"
                    />
                </div>
            </div>
        </Dialog>
    );
}
